import { Link, createFileRoute } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihLandingHero } from "~/components/cih/landing-hero";
import { LibraryLink } from "~/components/cih/library-link";
import { CihReveal } from "~/components/cih/reveal";
import { Button } from "~/components/ui/button";
import { NamedIcon } from "~/components/ui/named-icon";
import {
  getPublishedGuidesFn,
  type GuideRow,
} from "~/lib/functions/cih/guides";
import {
  getPublishedPlaybooksFn,
  type PlaybookWithRelations,
} from "~/lib/functions/cih/playbooks";
import { GuideType } from "~/lib/schemas/db-enums-cih";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  buildGuidesIndexSections,
  type LibraryEntry,
  type LibrarySection,
  type LibrarySectionKey,
} from "~/lib/shared/cih/library-shelves";
import {
  GUIDE_TYPE_LABELS,
  LIBRARY_SHELF_LABELS,
  guideTypeSchema,
} from "~/lib/shared/content-status";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";
import { READINESS_PUBLIC_PATH } from "~/lib/shared/readiness/routes";

const CANONICAL_URL = `${BRAND_URL}/coaching-business`;

export const Route = createFileRoute("/_public/coaching-business/")({
  staticData: { title: "Coaching Business" },
  head: () => ({
    ...createBrandMeta({
      title: "Coaching Business Guides | Dream Coach Match",
      description:
        "Tactical, stage-matched guides for coaches building their practice — client acquisition, pricing, systems, and scaling.",
      ogUrl: CANONICAL_URL,
      canonical: CANONICAL_URL,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Coaching Business", url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  loader: async () => {
    const [guides, playbooks] = await Promise.all([
      getPublishedGuidesFn(),
      getPublishedPlaybooksFn(),
    ]);
    return { guides, playbooks };
  },
  component: CoachingBusinessPage,
});

type GuideWithSlug = GuideRow & { slug: string };
type PlaybookWithSlug = PlaybookWithRelations & { slug: string };
type LibraryEntryUnion = LibraryEntry<GuideWithSlug, PlaybookWithSlug>;
type LibrarySectionUnion = LibrarySection<GuideWithSlug, PlaybookWithSlug>;

/**
 * Signpost copy per rendered section — progression language only. A shelf
 * mixes multiple revenue-band floors (see the migration plan's measured data),
 * so nothing here may read as, or imply, a revenue cutoff.
 */
const SHELF_SIGNPOSTS: Partial<Record<LibrarySectionKey, string>> = {
  starting_out: "First clients, first offer, first proof it works.",
  getting_consistent:
    "A repeatable pipeline that doesn't depend on referrals alone.",
  building: "Systems and structure so growth doesn't run through you alone.",
  scaling: "Team, leverage, and infrastructure beyond your own hours.",
  spine_companion: "Two more entry points into the same roadmap.",
};

/**
 * Reader-facing section heading. The shelf enum's own labels are the sheet's
 * internal names ("Spine (companion)") and are right for the admin editor, but
 * must never reach a public page — so every key is spelled out here rather
 * than falling through to `LIBRARY_SHELF_LABELS`.
 */
function sectionLabel(key: LibrarySectionKey): string {
  switch (key) {
    case "spine_hero":
    case "spine_companion":
      return "All stages";
    case "starting_out":
    case "getting_consistent":
    case "building":
    case "scaling":
    case "canon":
      return LIBRARY_SHELF_LABELS[key];
    case "start_here":
      return "Start here";
    case "unshelved":
      return "More guides";
  }
}

/** Small uppercase badge for non-stage-guide types. Stage guides get a larger card instead. */
function guideTypeBadge(guideType: string): string | null {
  if (guideType === GuideType.StageGuide) return null;
  const parsed = guideTypeSchema.safeParse(guideType);
  return parsed.success ? GUIDE_TYPE_LABELS[parsed.data] : null;
}

function isStageGuideEntry(entry: LibraryEntryUnion): boolean {
  return (
    entry.type === "guide" && entry.item.guideType === GuideType.StageGuide
  );
}

function CoachingBusinessPage() {
  const { guides, playbooks } = Route.useLoaderData();

  const guidesWithSlug = guides.filter(
    (g): g is GuideWithSlug => g.slug != null,
  );
  const playbooksWithSlug = playbooks.filter(
    (p): p is PlaybookWithSlug => p.slug != null,
  );

  const sections = buildGuidesIndexSections(guidesWithSlug, playbooksWithSlug);
  const heroSection = sections.find((s) => s.key === "spine_hero");
  const flagship = heroSection?.items[0] ?? null;
  // The hero shelf holds one row today, and only the first gets the flagship
  // card. Any others still have to render — dropping the section wholesale is
  // how a published page silently disappears while the sitemap still emits it.
  const heroOverflow = (heroSection?.items ?? []).slice(1);
  const restSections = [
    ...(heroOverflow.length > 0
      ? [{ key: "spine_hero" as const, items: heroOverflow }]
      : []),
    ...sections.filter((s) => s.key !== "spine_hero"),
  ];

  return (
    <>
      <CihLandingHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Coaching Business" },
        ]}
        accent="green"
        eyebrow="Coaching Intelligence"
        title="Build your coaching business, stage by stage"
        lede="Tactical, stage-matched guides for every phase of your practice — from your first paying clients to systems that scale past $10K months."
        cta={
          <>
            <Link to={READINESS_PUBLIC_PATH}>
              <Button variant="primary" size="lg">
                Take the diagnostic →
              </Button>
            </Link>
            {flagship ? (
              <LibraryLink entry={flagship}>
                <Button variant="default" size="lg">
                  Read the roadmap
                </Button>
              </LibraryLink>
            ) : null}
          </>
        }
      >
        {restSections.length > 1 ? (
          <nav
            aria-label="Practice shelves"
            className="mx-auto mt-12 flex flex-wrap items-center justify-center gap-2"
          >
            {restSections.map((section) => (
              <a
                key={section.key}
                href={`#shelf-${section.key}`}
                className="rounded-full border border-gray-200 bg-white/70 px-4 py-2 text-sm font-medium text-gray-700 backdrop-blur transition-colors hover:border-green-300 hover:text-green-800"
              >
                {sectionLabel(section.key)}
              </a>
            ))}
          </nav>
        ) : null}
      </CihLandingHero>

      {sections.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {flagship ? <FlagshipSection entry={flagship} /> : null}
          {restSections.map((section, index) => (
            <ShelfSection
              key={section.key}
              section={section}
              isFirst={!flagship && index === 0}
            />
          ))}
        </>
      )}

      {playbooks.length > 0 ? <PlaybooksBand count={playbooks.length} /> : null}

      <CihClosingCta
        kicker="Coach Business Builder"
        headline="Build the practice you actually want."
        cta={
          <Link to={READINESS_PUBLIC_PATH}>
            <Button variant="primary" size="lg">
              Take the diagnostic →
            </Button>
          </Link>
        }
      />
    </>
  );
}

function FlagshipSection({ entry }: { readonly entry: LibraryEntryUnion }) {
  return (
    <section className="mx-auto max-w-7xl px-4 pt-14 pb-14 md:pt-20 md:pb-20 xl:px-0">
      <CihReveal>
        <LibraryLink
          entry={entry}
          className="group relative col-span-full flex flex-col gap-4 overflow-hidden rounded-3xl border border-green-900 bg-gradient-to-br from-green-800 via-green-900 to-emerald-950 p-8 shadow-md transition duration-300 hover:-translate-y-0.5 hover:shadow-lg md:p-12"
        >
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 right-[-6%] h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="absolute -bottom-32 left-[10%] h-72 w-72 rounded-full bg-teal-400/10 blur-3xl" />
          </div>
          <div className="relative text-[10px] font-medium tracking-[0.22em] text-emerald-300 uppercase">
            The full journey
          </div>
          <h3 className="relative max-w-3xl font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
            {entry.item.title}
          </h3>
          {entry.item.description ? (
            <p className="relative max-w-2xl text-base leading-relaxed text-emerald-100/90">
              {entry.item.description}
            </p>
          ) : null}
          <span className="relative mt-2 text-sm font-medium text-white">
            Read the roadmap{" "}
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
              →
            </span>
          </span>
        </LibraryLink>
      </CihReveal>
    </section>
  );
}

function ShelfSection({
  section,
  isFirst,
}: {
  readonly section: LibrarySectionUnion;
  readonly isFirst: boolean;
}) {
  const quiet = section.key === "spine_companion";
  const signpost = SHELF_SIGNPOSTS[section.key];

  return (
    <section
      id={`shelf-${section.key}`}
      className={`mx-auto max-w-7xl scroll-mt-24 px-4 xl:px-0 ${
        quiet
          ? "pt-10 pb-16 md:pt-12 md:pb-20"
          : "pt-14 pb-14 md:pt-20 md:pb-20"
      } ${isFirst ? "" : "border-t border-gray-100"}`}
    >
      <CihReveal>
        <h2
          className={`font-heading font-medium tracking-tight text-gray-900 ${
            quiet ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"
          }`}
        >
          {sectionLabel(section.key)}
        </h2>
        {signpost ? (
          <p className="mt-2 max-w-xl text-base leading-normal text-gray-500">
            {signpost}
          </p>
        ) : null}
      </CihReveal>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
        {section.items.map((entry) => (
          <LibraryCard
            key={`${entry.type}-${entry.item.slug}`}
            entry={entry}
            large={isStageGuideEntry(entry)}
            wide={section.items.length === 1}
          />
        ))}
      </div>
    </section>
  );
}

function LibraryCard({
  entry,
  large,
  wide = false,
}: {
  readonly entry: LibraryEntryUnion;
  readonly large: boolean;
  readonly wide?: boolean;
}) {
  const badge =
    entry.type === "guide" ? guideTypeBadge(entry.item.guideType) : "Playbook";

  return (
    <LibraryLink
      entry={entry}
      className={`group flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-7 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md md:p-8 ${
        large || wide ? "md:col-span-2" : ""
      }`}
    >
      {badge ? (
        <div className="text-[10px] font-medium tracking-[0.22em] text-gray-400 uppercase">
          {badge}
        </div>
      ) : null}
      <h3
        className={`font-heading font-medium tracking-tight text-gray-900 ${
          large ? "text-3xl" : "text-2xl"
        }`}
      >
        {entry.item.title}
      </h3>
      {entry.item.description ? (
        <p className="max-w-2xl text-sm leading-relaxed text-gray-500">
          {entry.item.description}
        </p>
      ) : null}
      <span className="mt-auto pt-3 text-sm font-medium text-gray-900">
        Read{" "}
        <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
          →
        </span>
      </span>
    </LibraryLink>
  );
}

function PlaybooksBand({ count }: { readonly count: number }) {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-16 md:pb-20 xl:px-0">
      <CihReveal>
        <Link
          to="/coaching-business/playbooks"
          className="group flex flex-col gap-3 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 via-white to-white p-8 transition duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md md:p-10"
        >
          <div className="text-[10px] font-medium tracking-[0.22em] text-green-700 uppercase">
            Also in the library
          </div>
          <h3 className="font-heading text-2xl font-medium tracking-tight text-gray-900 md:text-3xl">
            Playbooks: frameworks worth knowing
          </h3>
          <p className="max-w-2xl text-sm leading-relaxed text-gray-500 md:text-base">
            The business and marketing frameworks the best coaches reference —
            analyzed through the lens of building a coaching practice.
          </p>
          <span className="mt-1 text-sm font-medium text-gray-900">
            Browse {count} {count === 1 ? "playbook" : "playbooks"}{" "}
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
              →
            </span>
          </span>
        </Link>
      </CihReveal>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="mx-auto max-w-md px-4 py-20 text-center">
      <NamedIcon name="target" className="mx-auto h-10 w-10 text-gray-300" />
      <p className="mt-4 text-sm text-gray-500">
        No guides published yet. Check back soon.
      </p>
    </section>
  );
}
